import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { Cancel01Icon } from '@hugeicons/core-free-icons'
import { cn } from '@/lib/utils'
import type { ScrollDepthSummary } from '@/lib/utils/heatmapUtils'
import { HeatmapOverlay, type HeatmapOverlayHandle, type HeatmapPoint } from './HeatmapOverlay'
import {
  SCROLL_REACH_LINES,
  depthAtReach,
  elementName,
  formatCount,
  heatmapElementKey,
  reachAtDepth,
  type HeatmapDevice,
  type HeatmapElement,
} from './heatmapInsights'

export type HeatmapCaptureHandle = {
  getImageElement: () => HTMLImageElement | null
  getHaloCanvas: () => HTMLCanvasElement | null
  getMainCanvas: () => HTMLCanvasElement | null
}

export type HeatmapCaptureLayer = 'clicks' | 'scroll' | 'dots'

type SelectedElementPopover = {
  element: HeatmapElement
  totalElements: number
  insight: string
  device: HeatmapDevice
}

interface HeatmapCaptureProps {
  screenshotUrl: string
  pageUrl: string
  device: HeatmapDevice
  layer: HeatmapCaptureLayer
  points: HeatmapPoint[]
  scrollDepth: ScrollDepthSummary
  opacity: number
  /** Ranked elements; the top ones get numbered share labels. */
  elements: HeatmapElement[]
  selected: SelectedElementPopover | null
  onSelectElement: (key: string | null) => void
  /** Element hovered in the ranked list: spotlit on the capture. */
  hoveredKey?: string | null
  /** End of the first screen, % of page height (scroll layer). */
  foldDepth?: number | null
  /** Natural capture size once the image decodes ("1440px wide", the fold). */
  onNaturalSize?: (size: { width: number; height: number }) => void
}

/** Labels on the capture match the ranked list beside it. */
const LABELLED = 8

const OUTLINE_PAD = 10
const POPOVER_WIDTH = 272

/** Element extent on the capture, in px, padded like Paper's 2px ink outline. */
function outlineRect(el: HeatmapElement, w: number, h: number) {
  let left = (el.minX / 100) * w - OUTLINE_PAD
  let right = (el.maxX / 100) * w + OUTLINE_PAD
  let top = (el.minY / 100) * h - OUTLINE_PAD
  let bottom = (el.maxY / 100) * h + OUTLINE_PAD
  if (right - left < 40) {
    const cx = (left + right) / 2
    left = cx - 20
    right = cx + 20
  }
  if (bottom - top < 28) {
    const cy = (top + bottom) / 2
    top = cy - 14
    bottom = cy + 14
  }
  return { left, top, width: right - left, height: bottom - top }
}

/**
 * The page capture on the canvas (Paper "Heatmaps · Simple · …"): the
 * screenshot, then one of three layers — warm click heat with numbered badges
 * on the top three hotspots, scroll reach shading with 75 / 50 / 25% lines, or
 * one dot per click while the page is still collecting. Clicking a hotspot
 * outlines it and opens its popover.
 */
export const HeatmapCapture = forwardRef<HeatmapCaptureHandle, HeatmapCaptureProps>(
  (
    {
      screenshotUrl,
      pageUrl,
      device,
      layer,
      points,
      scrollDepth,
      opacity,
      elements,
      selected,
      onSelectElement,
      hoveredKey = null,
      foldDepth = null,
      onNaturalSize,
    },
    ref,
  ) => {
    const imageRef = useRef<HTMLImageElement>(null)
    const overlayRef = useRef<HeatmapOverlayHandle>(null)
    const popoverRef = useRef<HTMLDivElement>(null)
    const [size, setSize] = useState<{ width: number; height: number } | null>(null)
    const [popoverHeight, setPopoverHeight] = useState(0)

    useImperativeHandle(ref, () => ({
      getImageElement: () => imageRef.current,
      getHaloCanvas: () => overlayRef.current?.getHaloCanvas() || null,
      getMainCanvas: () => overlayRef.current?.getMainCanvas() || null,
    }))

    const measure = useCallback(() => {
      const img = imageRef.current
      if (!img || !img.complete || !img.naturalWidth) return
      setSize({ width: img.offsetWidth, height: img.offsetHeight })
    }, [])

    useEffect(() => {
      const img = imageRef.current
      if (!img) return
      measure()
      const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
      observer?.observe(img)
      return () => observer?.disconnect()
    }, [measure, screenshotUrl])

    useLayoutEffect(() => {
      setPopoverHeight(popoverRef.current?.offsetHeight ?? 0)
    }, [selected])

    useEffect(() => {
      if (!selected) return
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onSelectElement(null)
      }
      window.addEventListener('keydown', onKey)
      return () => window.removeEventListener('keydown', onKey)
    }, [selected, onSelectElement])

    const keyForPoint = useCallback((point: HeatmapPoint) => heatmapElementKey(point), [])

    const labelled = useMemo(() => elements.slice(0, LABELLED), [elements])
    const hovered = hoveredKey ? elements.find((e) => e.key === hoveredKey) : undefined
    const w = size?.width ?? 0
    const h = size?.height ?? 0

    const selectedRect = selected && size ? outlineRect(selected.element, w, h) : null
    const popoverPos = (() => {
      if (!selectedRect) return null
      let left = selectedRect.left + selectedRect.width + 13
      let top = selectedRect.top - 67
      if (left + POPOVER_WIDTH > w + 24) left = selectedRect.left - 13 - POPOVER_WIDTH
      if (left < 8) {
        left = Math.min(Math.max(8, selectedRect.left), Math.max(8, w - POPOVER_WIDTH - 8))
        top = selectedRect.top + selectedRect.height + 8
      }
      top = Math.max(8, Math.min(top, h - popoverHeight - 8))
      return { left, top }
    })()

    return (
      <div
        className={cn(
          'relative overflow-clip rounded-xl bg-card shadow-[0_0_0_1px_rgb(9_9_11/0.05)]',
          device === 'mobile' ? 'w-[390px] max-w-full' : 'w-full max-w-[800px]',
        )}
      >
        <img
          ref={imageRef}
          src={screenshotUrl}
          alt={`Capture of ${pageUrl}`}
          className="block h-auto w-full select-none"
          draggable={false}
          onLoad={(e) => {
            measure()
            onNaturalSize?.({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })
          }}
        />

        {layer === 'clicks' && size && (
          <>
            {/* Dim the page so heat and labels carry the view, as in Clarity. */}
            <div aria-hidden className="pointer-events-none absolute inset-0 z-[1] bg-neutral-950/40" />
            <HeatmapOverlay
              ref={overlayRef}
              heatmapData={points}
              imageWidth={w}
              imageHeight={h}
              imageLeft={0}
              imageTop={0}
              pageUrl={pageUrl}
              deviceType={device}
              opacity={opacity}
              onPointClick={(point) => onSelectElement(keyForPoint(point))}
            />
            {hovered && !selected && (
              <div
                aria-hidden
                className="pointer-events-none absolute z-[4] rounded-lg border-2 border-white shadow-[0_0_0_9999px_rgb(9_9_11/0.25)]"
                style={outlineRect(hovered, w, h)}
              />
            )}
            {labelled.map((el) => {
              if (selected?.element.key === el.key) return null
              const r = outlineRect(el, w, h)
              const active = hovered?.key === el.key
              return (
                <button
                  key={el.key}
                  type="button"
                  onClick={() => onSelectElement(el.key)}
                  aria-label={`Hotspot ${el.rank}: ${elementName(el) ?? 'unlabelled element'}, ${Math.round(el.share)}% of ${device === 'mobile' ? 'taps' : 'clicks'}`}
                  className={cn(
                    'absolute z-[5] flex h-5 -translate-x-1/2 -translate-y-1/2 items-center gap-1 whitespace-nowrap rounded-full py-0 pl-0.5 pr-1.5 text-[11px] font-semibold leading-4 tabular-nums shadow-[0_0_0_1px_rgb(0_0_0/0.12),0_1px_3px_rgb(0_0_0/0.25)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    active ? 'bg-neutral-950 text-white' : 'bg-white text-neutral-950',
                  )}
                  style={{ left: Math.min(Math.max(r.left + r.width - 5, 28), w - 28), top: Math.max(r.top + 5, 12) }}
                >
                  <span
                    className={cn(
                      'flex size-4 items-center justify-center rounded-full text-[10px]',
                      active ? 'bg-white text-neutral-950' : 'bg-neutral-950 text-white',
                    )}
                  >
                    {el.rank}
                  </span>
                  {formatShare(el.share)}
                </button>
              )
            })}
          </>
        )}

        {layer === 'dots' && size && (
          <div className="pointer-events-none absolute inset-0 z-[2]" aria-hidden>
            {points.flatMap((p, i) => {
              const n = Math.min(p.count || p.value || 1, 5)
              return Array.from({ length: n }, (_, j) => (
                <span
                  key={`${i}-${j}`}
                  className="absolute size-[7px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-neutral-900 shadow-[0_0_0_1.5px_#fff,0_0_0_2.5px_rgb(0_0_0/0.15)]"
                  style={{
                    left: (p.x / 100) * w + (j % 2 ? 5 : -5) * Math.ceil(j / 2),
                    top: (p.y / 100) * h + (j % 2 ? 3 : -3) * Math.ceil(j / 2),
                  }}
                />
              ))
            })}
          </div>
        )}

        {layer === 'scroll' && size && (
          <ScrollReachLayer scrollDepth={scrollDepth} height={h} foldDepth={foldDepth} device={device} />
        )}

        {selected && selectedRect && popoverPos && (
          <>
            <div
              aria-hidden
              className="pointer-events-none absolute z-[4] rounded-lg border-2 border-neutral-950"
              style={selectedRect}
            />
            <div
              ref={popoverRef}
              role="dialog"
              aria-label={elementName(selected.element) ?? 'Selected element'}
              className="absolute z-[5] flex w-[272px] flex-col gap-3.5 rounded-xl bg-popover p-4 shadow-[0_0_0_1px_rgb(0_0_0/0.09),0_8px_24px_rgb(0_0_0/0.06)]"
              style={popoverPos}
            >
              <div className="flex items-start gap-2">
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <div className="text-sm font-semibold leading-5 text-ui-title">
                    {elementName(selected.element) ?? 'Unlabelled element'}
                  </div>
                  {selected.element.secondary && elementName(selected.element) !== selected.element.secondary && (
                    <div className="truncate font-mono text-[12px] leading-4 text-muted-foreground">
                      {selected.element.secondary}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onSelectElement(null)}
                  aria-label="Close"
                  className="shrink-0 rounded-sm text-foreground/45 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <HugeiconsIcon icon={Cancel01Icon} size={14} />
                </button>
              </div>
              <div className="flex gap-3 border-t border-border pt-3">
                {[
                  { value: formatCount(selected.element.clicks), label: selected.device === 'mobile' ? 'Taps' : 'Clicks' },
                  { value: `${Math.round(selected.element.share)}%`, label: selected.device === 'mobile' ? 'Of taps' : 'Of clicks' },
                  { value: `#${selected.element.rank}`, label: `Of ${selected.totalElements}` },
                ].map((stat) => (
                  <div key={stat.label} className="flex w-[72px] shrink-0 flex-col gap-0.5">
                    <div className="text-[22px] font-semibold leading-7 tracking-[-0.02em] tabular-nums text-ui-title">
                      {stat.value}
                    </div>
                    <div className="text-[12px] leading-4 text-muted-foreground">{stat.label}</div>
                  </div>
                ))}
              </div>
              <p className="text-[13px] leading-[18px] text-muted-foreground">{selected.insight}</p>
            </div>
          </>
        )}
      </div>
    )
  },
)

HeatmapCapture.displayName = 'HeatmapCapture'

/**
 * Ink shading deepens past each reach line; 50% is the average fold. Marks on
 * the capture use fixed ink in both themes: they sit on a light page screenshot.
 */
const BAND_SHADE: Record<number, string> = {
  75: 'bg-neutral-900/[0.04]',
  50: 'bg-neutral-900/[0.10]',
  25: 'bg-neutral-900/[0.18]',
}

function ScrollReachLayer({
  scrollDepth,
  height,
  foldDepth,
  device,
}: {
  scrollDepth: ScrollDepthSummary
  height: number
  foldDepth: number | null
  device: HeatmapDevice
}) {
  const lines = SCROLL_REACH_LINES.map((reach) => ({ reach, depth: depthAtReach(scrollDepth, reach) })).filter(
    (l): l is { reach: (typeof SCROLL_REACH_LINES)[number]; depth: number } => l.depth !== null,
  )
  if (!scrollDepth || scrollDepth.totalSessions === 0) return null

  const foldTop = foldDepth !== null ? (foldDepth / 100) * height : null
  const pastFold = foldDepth !== null ? reachAtDepth(scrollDepth, Math.min(100, foldDepth + 1)) : null

  return (
    <div className="pointer-events-none absolute inset-0 z-[2]">
      {lines.map((line, i) => {
        const top = (line.depth / 100) * height
        const next = lines[i + 1]
        const bottom = next ? (next.depth / 100) * height : height
        return (
          <div key={line.reach}>
            <div className={cn('absolute inset-x-0', BAND_SHADE[line.reach])} style={{ top, height: bottom - top }} />
            <div className="absolute inset-x-0 h-px bg-neutral-950/[0.25]" style={{ top }} />
          </div>
        )
      })}
      {/* Paper sits it under a typical site nav */}
      <ReachChip top={62}>100% reached</ReachChip>
      {lines.map((line) => (
        <ReachChip key={line.reach} top={(line.depth / 100) * height + 6}>
          {line.reach}% reached
        </ReachChip>
      ))}
      {foldTop !== null && (
        <>
          <div className="absolute inset-x-0 border-t-2 border-dashed border-neutral-950" style={{ top: foldTop }} />
          <ReachChip top={foldTop - 30} fold side="left">
            Fold · end of first {device === 'mobile' ? 'phone' : 'laptop'} screen
            {pastFold !== null && ` · ${Math.round(pastFold)}% scroll past`}
          </ReachChip>
        </>
      )}
    </div>
  )
}

function ReachChip({
  top,
  fold,
  side = 'right',
  children,
}: {
  top: number
  fold?: boolean
  side?: 'left' | 'right'
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        side === 'left' ? 'left-3' : 'right-3',
        'absolute flex h-6 items-center rounded-[7px] px-2 text-[12px] font-medium leading-4 shadow-[0_0_0_1px_rgb(0_0_0/0.08)]',
        fold ? 'bg-neutral-950 text-white' : 'bg-white text-neutral-950',
      )}
      style={{ top }}
    >
      {children}
    </div>
  )
}

/** "36%" on a capture label; "<1%" rather than 0. */
function formatShare(share: number): string {
  return share > 0 && share < 1 ? '<1%' : `${Math.round(share)}%`
}
